// The 50 prompts from "Steal this: 50 AI content prompts" (Gabby Torres-Soler, Sked Social),
// rewritten as fillable templates. Pure data plus one pure fill function, shared by the
// Worker-rendered SEO pages, the builder island, the pack page and the PDF.
//
// Body tokens are {slot} names from SlotKey. A token that is not a SlotKey is left in the
// text untouched, so keep bodies to known keys.

import type { DuotoneName } from "./duotone-icons";

export const CATEGORIES = [
  "brand-story",
  "authority",
  "engagement",
  "conversion",
  "short-video",
] as const;
export type Category = (typeof CATEGORIES)[number];

export const PLATFORMS = ["instagram", "linkedin", "tiktok", "facebook", "youtube-shorts"] as const;
export type Platform = (typeof PLATFORMS)[number];

export const BUSINESS_TYPES = [
  "agency",
  "in-house",
  "multi-location",
  "ecommerce",
  "hospitality",
  "creator",
] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const SLOT_KEYS = [
  "brand",
  "audience",
  "platform",
  "industry",
  "role",
  "feature",
  "value",
  "topic",
  "metric",
  "process",
  "workflow",
  "channel",
  "problem",
] as const;
export type SlotKey = (typeof SLOT_KEYS)[number];

export interface CategoryMeta {
  id: Category;
  /** Short name for chips, tabs and the goal picker. */
  name: string;
  /** Font Awesome duotone regular icon, vendored in duotone-icons.ts. */
  icon: DuotoneName;
  /** URL segment under the tool's base path. */
  slug: string;
  /** H1-ready page title. */
  title: string;
  /** One-line intro under the H1. */
  intro: string;
}

export const CATEGORY_META: Record<Category, CategoryMeta> = {
  "brand-story": {
    id: "brand-story",
    name: "Brand story",
    icon: "book-open",
    slug: "brand-story-prompts",
    title: "10 AI prompts for brand story posts",
    intro:
      "Origin stories, customer heroes and the calls you got wrong. Prompts that make your brand sound like people.",
  },
  authority: {
    id: "authority",
    name: "Authority",
    icon: "chess-knight",
    slug: "authority-prompts",
    title: "10 AI prompts that make you the expert",
    intro:
      "Plans, frameworks and hard-won lessons. Prompts that show what you know without a week of writing.",
  },
  engagement: {
    id: "engagement",
    name: "Engagement",
    icon: "comments",
    slug: "engagement-prompts",
    title: "10 AI prompts that get people commenting",
    intro:
      "Polls, debates and fill-in-the-blanks. Prompts built to start conversations, not collect likes.",
  },
  conversion: {
    id: "conversion",
    name: "Conversion",
    icon: "bullseye",
    slug: "conversion-prompts",
    title: "10 AI prompts that sell without sounding salesy",
    intro:
      "Problems, proof and a soft invite. Prompts that make your product the obvious next step.",
  },
  "short-video": {
    id: "short-video",
    name: "Short-form video",
    icon: "clapperboard-play",
    slug: "short-form-video-prompts",
    title: "10 AI prompts for Reels, TikTok and Shorts",
    intro:
      "Hooks, scripts and shot lists. Prompts that get you from blank screen to filming today.",
  },
};

export interface PlatformMeta {
  id: Platform;
  label: string;
}

export const PLATFORM_META: Record<Platform, PlatformMeta> = {
  instagram: { id: "instagram", label: "Instagram" },
  linkedin: { id: "linkedin", label: "LinkedIn" },
  tiktok: { id: "tiktok", label: "TikTok" },
  facebook: { id: "facebook", label: "Facebook" },
  "youtube-shorts": { id: "youtube-shorts", label: "YouTube Shorts" },
};

export interface BusinessTypeMeta {
  id: BusinessType;
  label: string;
}

export const BUSINESS_TYPE_META: Record<BusinessType, BusinessTypeMeta> = {
  agency: { id: "agency", label: "Agency" },
  "in-house": { id: "in-house", label: "In-house brand team" },
  "multi-location": { id: "multi-location", label: "Multi-location or franchise" },
  ecommerce: { id: "ecommerce", label: "Ecommerce brand" },
  hospitality: { id: "hospitality", label: "Hotel, restaurant or venue" },
  creator: { id: "creator", label: "Creator or personal brand" },
};

export interface SlotMeta {
  key: SlotKey;
  /** Field label in the builder. */
  label: string;
  /** Shown in brackets when the slot is empty, e.g. "[your audience]". */
  placeholder: string;
  /** Fallback example when a template has no default of its own. */
  example: string;
}

export const SLOT_META: Record<SlotKey, SlotMeta> = {
  brand: {
    key: "brand",
    label: "Brand name",
    placeholder: "your brand",
    example: "Little Lantern Coffee",
  },
  audience: {
    key: "audience",
    label: "Who it's for",
    placeholder: "your audience",
    example: "people who make coffee at home",
  },
  platform: {
    key: "platform",
    label: "Platform",
    placeholder: "your platform",
    example: "Instagram",
  },
  industry: {
    key: "industry",
    label: "Industry",
    placeholder: "your industry",
    example: "specialty coffee",
  },
  role: {
    key: "role",
    label: "Role",
    placeholder: "role",
    example: "head roaster",
  },
  feature: {
    key: "feature",
    label: "Product, service or feature",
    placeholder: "your product or feature",
    example: "our coffee subscription",
  },
  value: {
    key: "value",
    label: "Company value",
    placeholder: "the value",
    example: "quality over speed",
  },
  topic: {
    key: "topic",
    label: "Topic",
    placeholder: "your topic",
    example: "brewing better coffee at home",
  },
  metric: {
    key: "metric",
    label: "Metric or term",
    placeholder: "the metric or term",
    example: "espresso extraction",
  },
  process: {
    key: "process",
    label: "Process",
    placeholder: "the process",
    example: "dialling in a new bag of beans",
  },
  workflow: {
    key: "workflow",
    label: "Workflow",
    placeholder: "the workflow",
    example: "planning and approving our social posts",
  },
  channel: {
    key: "channel",
    label: "Channel, tool or product",
    placeholder: "the channel or tool",
    example: "your coffee grinder",
  },
  problem: {
    key: "problem",
    label: "Problem",
    placeholder: "the problem",
    example: "coffee that tastes bitter or sour",
  },
};

/** Example values a page can show in place of a visitor's own. */
export type SlotDefaults = Partial<Record<Exclude<SlotKey, "platform">, string>>;

export interface TemplateVariant {
  body: string;
  /** Replaces the template's platforms when the rewrite suits different ones. */
  platforms?: readonly Platform[];
  /** Merged over the template's defaults. */
  defaults?: SlotDefaults;
}

export interface PromptTemplate {
  /** Stable kebab-case slug. Never rename: boards and packs store it. */
  id: string;
  category: Category;
  /** Font Awesome duotone regular icon, vendored in duotone-icons.ts. */
  icon: DuotoneName;
  title: string;
  /** One line for SEO cards. */
  summary: string;
  /** Platforms this prompt suits, best fit first. */
  platforms: readonly Platform[];
  body: string;
  /** Rewrites for business types the base body does not fit. */
  variants?: Partial<Record<BusinessType, TemplateVariant>>;
  defaults?: SlotDefaults;
}

const ALL_PLATFORMS: readonly Platform[] = PLATFORMS;
const POST_PLATFORMS: readonly Platform[] = ["instagram", "linkedin", "facebook"];
const LINKEDIN_FIRST: readonly Platform[] = ["linkedin", "instagram", "facebook"];
const CAROUSEL_PLATFORMS: readonly Platform[] = ["instagram", "linkedin", "facebook", "tiktok"];
const COMMENT_PLATFORMS: readonly Platform[] = ["instagram", "linkedin", "facebook", "tiktok"];
const VIDEO_PLATFORMS: readonly Platform[] = ["tiktok", "instagram", "youtube-shorts", "facebook"];

export const TEMPLATES: readonly PromptTemplate[] = [
  // Brand story
  {
    id: "origin-story-carousel",
    category: "brand-story",
    icon: "images",
    title: "Origin story carousel",
    summary: "Turn the moment you saw a broken problem into a 7-slide carousel.",
    platforms: CAROUSEL_PLATFORMS,
    body: "You are a senior brand storyteller for {brand}. Write a 7-slide {platform} carousel script about the moment we realised something in {industry} was broken: {problem}. Explain why we decided to fix it. Make it conversational and clear, and write it for {audience}. Add a short caption with a hook and a call to save the post.",
    defaults: { problem: "most people had never tasted properly fresh coffee at home" },
  },
  {
    id: "founder-intro",
    category: "brand-story",
    icon: "user-tie",
    title: "Founder or leader intro",
    summary: "A first-person intro post: who you are, what you do and what you believe.",
    platforms: LINKEDIN_FIRST,
    body: "Act as a {platform} ghostwriter for the founder of {brand}. Write a first-person post that introduces who I am, what {brand} does, and the 3 core beliefs that guide how we work. Tone: confident, down to earth, slightly rebellious. Maximum 230 words. Use only what I tell you about myself and leave [brackets] for anything you don't know.",
    variants: {
      creator: {
        platforms: ["instagram", "tiktok", "linkedin"],
        body: "Act as a ghostwriter for me, a creator who makes content about {topic}. Write a first-person {platform} post that introduces who I am, what I make, who it's for, and the 3 beliefs behind my content. Tone: confident, down to earth, slightly rebellious. Maximum 230 words. Use only what I tell you about myself and leave [brackets] for anything you don't know.",
        defaults: { topic: "running for beginners" },
      },
    },
  },
  {
    id: "if-we-started-today",
    category: "brand-story",
    icon: "seedling",
    title: "If we started today",
    summary: "Share the exact plan you would follow if you had to rebuild from zero.",
    platforms: LINKEDIN_FIRST,
    body: "Write a {platform} post for {audience}: 'If we had to rebuild {topic} from zero in 90 days, here's exactly what we'd do.' Include a 5-step plan and invite readers to comment with their own non-negotiables.",
    defaults: { topic: "our social media presence", audience: "other small business owners" },
  },
  {
    id: "day-in-the-life",
    category: "brand-story",
    icon: "mug-hot",
    title: "Behind-the-scenes day in the life",
    summary: "A scene-by-scene video script following one person on your team.",
    platforms: VIDEO_PLATFORMS,
    body: "Write a script for a 45 to 60 second {platform} video showing a day in the life of our {role} at {brand}. Give scene-by-scene ideas with on-screen text and voiceover lines. Write it for {audience}.",
  },
  {
    id: "values-in-action",
    category: "brand-story",
    icon: "hand-holding-heart",
    title: "Values in action story",
    summary: "The time you chose a value over a quick win, and what it cost you.",
    platforms: LINKEDIN_FIRST,
    body: "Write a story post about a real decision at {brand} where we chose {value} even though it cost us in the short term. Format it for {platform} with a strong hook and a clear lesson. Leave [brackets] where I need to add the real details.",
  },
  {
    id: "we-were-wrong",
    category: "brand-story",
    icon: "eraser",
    title: "We were wrong about our audience",
    summary: "Admit an assumption about your audience that the data proved false.",
    platforms: LINKEDIN_FIRST,
    body: "Write a {platform} post titled 'We were wrong about our audience.' Explain a big assumption {brand} had about {audience} that turned out to be false, what data proved it, and how we changed our approach. Use only facts I give you and leave [brackets] for the numbers.",
  },
  {
    id: "customer-hero",
    category: "brand-story",
    icon: "medal",
    title: "Customer hero story",
    summary: "A carousel where your customer is the hero, not you.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Write a 6-slide {platform} carousel where the hero is our customer, not {brand}. They're a {role} struggling with {problem}. Show their before, the turning point and their after. End with a soft call to learn more. Leave [brackets] for real names, quotes and numbers.",
    defaults: { role: "office manager", problem: "a team running on bad instant coffee" },
  },
  {
    id: "how-we-changed",
    category: "brand-story",
    icon: "rotate",
    title: "Internal change story",
    summary: "How you changed the way you work, told through impact, not tools.",
    platforms: LINKEDIN_FIRST,
    body: "You are a content strategist. Draft a {platform} post telling the story of how {brand} changed the way we handle {workflow}. Focus on what it changed for our strategy, our team and our results, not the tools. Leave [brackets] where I need to add the real details.",
  },
  {
    id: "surprising-win",
    category: "brand-story",
    icon: "party-horn",
    title: "Most surprising win",
    summary: "The campaign that worked better than anyone expected, and why.",
    platforms: LINKEDIN_FIRST,
    body: "Write an engaging {platform} story post about the most unexpectedly successful campaign {brand} ran last year. Include our hypothesis, what we tested, what surprised us in the results and what we changed afterwards. Use only numbers I give you and leave [brackets] where they go.",
  },
  {
    id: "belief-manifesto",
    category: "brand-story",
    icon: "flag",
    title: "Brand belief manifesto",
    summary: "Ten bold, quotable beliefs about your field.",
    platforms: POST_PLATFORMS,
    body: "Write a punchy 10-point 'This is what we believe about {topic}' manifesto for {brand} on {platform}. Tone: expert, bold, no fluff. Each point should work as a standalone quote.",
  },

  // Authority
  {
    id: "thirty-day-plan",
    category: "authority",
    icon: "calendar-days",
    title: "30-day expert plan",
    summary: "'If I ran this for you for 30 days', a 5-point plan that shows you know your stuff.",
    platforms: POST_PLATFORMS,
    body: "Act as the most experienced {role} at {brand}. Write a {platform} post: 'If I were in charge of your {topic} for 30 days, here's the 5-point plan I'd follow.' Aim it at {audience}. Use clear, concrete actions, not generic advice.",
    defaults: { role: "barista trainer", topic: "home coffee setup" },
    variants: {
      agency: {
        platforms: LINKEDIN_FIRST,
        body: "Act as a fractional CMO at {brand}. Write a {platform} post: 'If I were your CMO for 30 days, here's the 5-point plan I'd use to fix your social strategy.' Aim it at {audience}. Use clear, concrete actions, not generic advice.",
        defaults: {
          brand: "Tidewater Studio",
          audience: "founders and marketing leads at growing brands",
        },
      },
      "multi-location": {
        platforms: ["linkedin", "facebook"],
        body: "Act as the head of marketing at {brand}, which runs many locations. Write a {platform} post: 'If I ran your location's social media for 30 days, here's the 5-point plan I'd follow.' Aim it at {audience}. Make every step something one location can do without waiting on head office.",
        defaults: {
          brand: "Parkside Physio",
          audience: "our clinic managers and franchise partners",
        },
      },
      hospitality: {
        platforms: ["instagram", "facebook"],
        body: "Act as the most experienced host at {brand}. Write a {platform} post: 'If your {topic} is 30 days away, here's the 5-point plan I'd follow.' Aim it at {audience}. Make each step specific to our venue and our area, not generic advice.",
        defaults: {
          brand: "The Saltbush Hotel",
          topic: "end-of-year work party",
          audience: "office managers planning a team celebration",
        },
      },
      ecommerce: {
        platforms: ["instagram", "tiktok", "facebook"],
        body: "Act as the product expert at {brand}. Write a {platform} post: 'If I had 30 days to sort out your {topic}, here's the 5-point plan I'd follow.' Aim it at {audience}. Give steps that work with or without our products, and mention {brand} only once.",
        defaults: {
          brand: "Kindred Skin",
          topic: "skincare routine",
          audience: "people with sensitive skin",
        },
      },
      creator: {
        platforms: ["instagram", "tiktok", "linkedin"],
        body: "Write a {platform} post in my voice as a creator: 'If I were coaching you on {topic} for 30 days, here's the 5-point plan I'd follow.' Aim it at {audience}. Use clear, concrete actions, not generic advice.",
        defaults: { topic: "running", audience: "people training for their first 10k" },
      },
    },
  },
  {
    id: "numbers-that-matter",
    category: "authority",
    icon: "chart-simple",
    title: "Numbers that matter",
    summary: "A carousel on the 5 numbers your audience should actually watch.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Write a {platform} carousel script titled '5 numbers every {role} should know about {topic}.' Explain each number in one line and why it matters to {audience}.",
    defaults: { role: "home barista", topic: "espresso" },
    variants: {
      agency: {
        platforms: ["linkedin", "instagram"],
        body: "Write a {platform} carousel script titled 'If you only tracked 5 social media metrics this quarter, make them these.' Explain each metric in one line and why it matters to {audience}.",
        defaults: { audience: "a CMO or head of marketing" },
      },
    },
  },
  {
    id: "monday-numbers",
    category: "authority",
    icon: "gauge-high",
    title: "Monday numbers breakdown",
    summary: "Walk through the 3 numbers you check every Monday and the calls they drive.",
    platforms: LINKEDIN_FIRST,
    body: "Draft a {platform} post where I walk through the 3 dashboards I check every Monday as the {role} at {brand}. For each one, list what I look at, what decisions it informs and one example decision from last quarter. Leave [brackets] for the real numbers.",
    defaults: { role: "head of marketing" },
    variants: {
      agency: {
        body: "Draft a {platform} post where I walk through the 3 reports {brand} checks for every client every Monday. For each one, list what we look at, what decisions it drives for the client and one real example from last quarter. Leave [brackets] for the real numbers.",
        defaults: { brand: "Tidewater Studio" },
      },
      "multi-location": {
        platforms: ["linkedin", "facebook"],
        body: "Draft a {platform} post where I walk through the 3 numbers I check across all our locations every Monday as the {role} at {brand}. For each one, explain what I look at, how it tells me which location needs help, and one decision it led to last quarter. Leave [brackets] for the real numbers.",
        defaults: { brand: "Parkside Physio", role: "marketing manager" },
      },
      hospitality: {
        platforms: ["instagram", "facebook", "linkedin"],
        body: "Draft a {platform} post where our {role} walks through the 3 things they check every Monday to plan the week at {brand}, such as bookings, reviews and what sold out. For each one, explain what they look at and one change it led to for guests. Keep it warm and behind the scenes, not corporate.",
        defaults: { brand: "The Saltbush Hotel", role: "venue manager" },
      },
      ecommerce: {
        platforms: ["linkedin", "instagram"],
        body: "Draft a {platform} post where I walk through the 3 numbers I check every Monday as the {role} at {brand}, such as best sellers, returns and the questions customers ask most. For each one, explain what I look at and one product or content decision it led to. Leave [brackets] for the real numbers.",
        defaults: { brand: "Kindred Skin", role: "founder" },
      },
      creator: {
        platforms: ["instagram", "tiktok", "linkedin"],
        body: "Draft a {platform} post where I walk through the 3 numbers I check every Monday as a creator. For each one, explain what I look at, what it tells me about {audience} and one content decision it changed last quarter. Leave [brackets] for the real numbers.",
        defaults: { audience: "people training for their first 10k" },
      },
    },
  },
  {
    id: "myth-vs-reality",
    category: "authority",
    icon: "magnifying-glass",
    title: "Myth vs reality",
    summary: "Bust 3 myths in your field with practical, real-world examples.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Write a 'Myth vs reality' {platform} post about {topic}. Include 3 myths and 3 realities, each with a practical example.",
  },
  {
    id: "quarterly-review",
    category: "authority",
    icon: "chart-line-up",
    title: "Quarterly review outline",
    summary: "The outline for a quarterly social media review your bosses will actually read.",
    platforms: ALL_PLATFORMS,
    body: "Create a bullet-point outline for a quarterly social media review deck for {brand}'s leadership team. Include the sections, the key charts and 3 to 5 questions the data should answer.",
    variants: {
      agency: {
        body: "Create a bullet-point outline for a quarterly social media review deck that {brand} will present to a client in {industry}. Include the sections, the key charts, what we recommend for next quarter, and 3 to 5 questions the client will ask that the data should answer.",
        defaults: { brand: "Tidewater Studio", industry: "hospitality" },
      },
      "multi-location": {
        body: "Create a bullet-point outline for a quarterly social media review deck for {brand}'s head office. Compare our locations side by side, show which ones need help, and include the key charts and 3 to 5 questions the data should answer.",
        defaults: { brand: "Parkside Physio" },
      },
      hospitality: {
        body: "Create a bullet-point outline for a quarterly social media review for {brand}. Tie social results to bookings, reviews and seasonal events, and include the key charts and 3 to 5 questions the data should answer before next season.",
        defaults: { brand: "The Saltbush Hotel" },
      },
      ecommerce: {
        body: "Create a bullet-point outline for a quarterly social media review deck for {brand}. Connect social results to site traffic, sales and product launches, and include the key charts and 3 to 5 questions the data should answer.",
        defaults: { brand: "Kindred Skin" },
      },
      creator: {
        body: "Create a bullet-point outline for a quarterly review of my content as a creator. Cover growth, what {audience} engaged with most, brand partnership results, and 3 to 5 questions to answer before I plan next quarter.",
        defaults: { audience: "people training for their first 10k" },
      },
    },
  },
  {
    id: "decision-checklist",
    category: "authority",
    icon: "clipboard-list-check",
    title: "Decision checklist",
    summary: "A simple 4-step checklist for what to do when something goes wrong.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Write a {platform} post that shares a simple framework for what to do when {problem}. Format it as a 4-step checklist. Tone: practical and non-judgemental.",
    defaults: { problem: "your espresso suddenly tastes sour" },
  },
  {
    id: "industry-forecast",
    category: "authority",
    icon: "binoculars",
    title: "Industry forecast",
    summary: "3 trends and 3 'do this, not that' calls for the year ahead.",
    platforms: LINKEDIN_FIRST,
    body: "You are a strategic leader at {brand}. Draft a {platform} post: 'What's changing in {industry} over the next 12 months.' Include 3 trends and 3 'do this, not that' recommendations for {audience}.",
  },
  {
    id: "behind-the-stack",
    category: "authority",
    icon: "layer-group",
    title: "Behind the stack",
    summary: "An honest breakdown of the tools you use and why each one earns its place.",
    platforms: ["linkedin", "facebook"],
    body: "Write a transparent {platform} post breaking down the tools {brand} uses for {process}. The tools are: [list your tools]. Explain why we set it up this way and how we decide whether a tool earns its place. Don't add tools I haven't named.",
    defaults: { process: "planning, scheduling and reporting on social media" },
  },
  {
    id: "guide-teaser",
    category: "authority",
    icon: "book-open-reader",
    title: "Guide teaser",
    summary: "Tease a free guide: who it's for and what readers will be able to do.",
    platforms: POST_PLATFORMS,
    body: "Draft a {platform} post teasing a new guide from {brand} about {topic}. Say who it's for, the problems it solves and 3 concrete things readers will be able to do after reading it. End with a soft call to comment 'GUIDE' to get it.",
  },
  {
    id: "lessons-learned",
    category: "authority",
    icon: "graduation-cap",
    title: "Lessons I learned the hard way",
    summary: "5 practical lessons for people earlier in their career than you.",
    platforms: LINKEDIN_FIRST,
    body: "Write a reflective {platform} post titled '5 lessons I wish I'd learned earlier as a {role}.' Aim it at people earlier in their career in {industry}. Make each lesson practical and tied to a real situation.",
    variants: {
      creator: {
        platforms: ["instagram", "tiktok", "linkedin"],
        body: "Write a reflective {platform} post titled '5 lessons I wish I'd learned earlier as a creator.' Aim it at people just starting to make content about {topic}. Make each lesson practical and tied to a real situation.",
        defaults: { topic: "running" },
      },
    },
  },

  // Engagement
  {
    id: "debate-questions",
    category: "engagement",
    icon: "messages-question",
    title: "Debate starter questions",
    summary: "10 thought-provoking (but friendly) questions that spark real debate.",
    platforms: COMMENT_PLATFORMS,
    body: "Generate 10 thought-provoking but non-toxic questions for {audience} that will spark debate on {platform}. Each should be answerable in a comment and relate to {topic}.",
  },
  {
    id: "poll-ideas",
    category: "engagement",
    icon: "square-poll-vertical",
    title: "Poll ideas",
    summary: "8 specific poll questions with ready-made answer options.",
    platforms: POST_PLATFORMS,
    body: "Create 8 {platform} poll questions about {topic} for {audience}, each with 3 or 4 answer options. Keep them specific, not vague.",
  },
  {
    id: "finish-this-sentence",
    category: "engagement",
    icon: "pen-line",
    title: "Finish this sentence",
    summary: "10 fill-in-the-blank prompts people can answer in one line.",
    platforms: COMMENT_PLATFORMS,
    body: "Write 10 'Finish this sentence' prompts for {platform} comments, aimed at {audience}. For example: 'The most overrated thing about {topic} is ____.' Keep them short and specific.",
  },
  {
    id: "productive-venting",
    category: "engagement",
    icon: "message-exclamation",
    title: "Venting, but productive",
    summary: "Let your audience vent, then turn it into a useful conversation.",
    platforms: POST_PLATFORMS,
    body: "Draft 5 {platform} post ideas that let {audience} vent about the frustrating parts of {topic}, then end each one with a constructive question that opens a real discussion.",
  },
  {
    id: "origin-stories-ask",
    category: "engagement",
    icon: "signs-post",
    title: "How did you get into it?",
    summary: "7 prompts that invite your audience to share their own stories.",
    platforms: POST_PLATFORMS,
    body: "Generate 7 {platform} post prompts that ask {audience} how they got into {topic}, what they wish they'd learned sooner, or the advice they'd give a beginner. Each prompt should invite stories in the comments.",
  },
  {
    id: "this-or-that",
    category: "engagement",
    icon: "circle-half-stroke",
    title: "This or that",
    summary: "10 two-option choices for carousels and Stories.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Create 10 'This or that' prompts for {platform} carousels or Stories, aimed at {audience}. Each should be a real choice people make about {topic}.",
  },
  {
    id: "quick-tip-swap",
    category: "engagement",
    icon: "handshake",
    title: "Quick tip swap",
    summary: "Offer a free quick tip to everyone who shares their challenge.",
    platforms: POST_PLATFORMS,
    body: "Draft 5 {platform} caption ideas where {brand} offers a free quick tip to anyone who shares their biggest challenge with {topic} in the comments. Tone: generous, not salesy.",
  },
  {
    id: "tell-us-without-telling-us",
    category: "engagement",
    icon: "face-smile-wink",
    title: "Tell us without telling us",
    summary: "7 inside-joke prompts your audience will want to answer.",
    platforms: COMMENT_PLATFORMS,
    body: "Write 7 'Tell us you're a {role} without telling us you're a {role}' prompts for {platform}, tailored to {audience}.",
    defaults: { role: "coffee snob" },
  },
  {
    id: "emoji-replies",
    category: "engagement",
    icon: "icons",
    title: "Emoji reply game",
    summary: "People comment an emoji, you reply with a tailored tip.",
    platforms: COMMENT_PLATFORMS,
    body: "Create 6 {platform} post ideas where people comment with an emoji and {brand} replies with a tailored tip, recommendation or friendly roast. Make each one relevant to {topic}.",
  },
  {
    id: "what-would-you-do",
    category: "engagement",
    icon: "person-circle-question",
    title: "What would you do?",
    summary: "10 short scenarios that ask your audience how they'd handle it.",
    platforms: POST_PLATFORMS,
    body: "Generate 10 short scenarios {audience} might face with {topic}, each followed by a question asking how others would handle it. Format them as {platform} captions.",
  },

  // Conversion
  {
    id: "problem-process-invite",
    category: "conversion",
    icon: "door-open",
    title: "Problem, process, invite",
    summary: "Name the problem, show your fix, then invite people in. No hype.",
    platforms: POST_PLATFORMS,
    body: "Write a {platform} caption that 1) names a specific problem {audience} face, such as {problem}, 2) shares our 3-step process for solving it, and 3) lightly invites people to see how {brand} does it. Avoid hype.",
  },
  {
    id: "feature-in-context",
    category: "conversion",
    icon: "puzzle-piece",
    title: "Feature in a real day",
    summary: "Show the before and after of one feature in a normal workday.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Create a 6-slide {platform} carousel script showing how {feature} fits into a typical day for a {role}. Show the before without it, then each step of the after with it. End with a soft call to message us for a walkthrough.",
    defaults: { role: "office manager" },
  },
  {
    id: "fastest-win",
    category: "conversion",
    icon: "stopwatch",
    title: "Fastest customer win",
    summary: "A case-study post about the quickest result a customer got.",
    platforms: POST_PLATFORMS,
    body: "Write a case-study style {platform} post about the fastest win a customer got with {brand}. Structure: the problem, what we did, the result and what it means for others like them. Use only details I give you and leave [brackets] for anything I need to fill in.",
  },
  {
    id: "freebie-giveaway",
    category: "conversion",
    icon: "gift",
    title: "Template giveaway",
    summary: "Give away a template or checklist you actually use.",
    platforms: POST_PLATFORMS,
    body: "Draft a {platform} post offering a free template or checklist {brand} actually uses: {topic}. Explain what it is, why we made it and how to get it, whether that's a comment, a message or the link in bio.",
    defaults: { topic: "our home brewing cheat sheet" },
  },
  {
    id: "old-way-new-way",
    category: "conversion",
    icon: "right-left",
    title: "Old way vs new way",
    summary: "Five bullets on the old way, five on the better way.",
    platforms: CAROUSEL_PLATFORMS,
    body: "Write a {platform} post that compares the old way and the new way of {workflow}, in 5 bullet points each. Use words {audience} already use, not jargon.",
    defaults: { workflow: "keeping the office stocked with good coffee" },
  },
  {
    id: "customer-spotlights",
    category: "conversion",
    icon: "users",
    title: "Customer type spotlights",
    summary: "5 post ideas, each showing a different kind of customer and their win.",
    platforms: POST_PLATFORMS,
    body: "Create 5 short {platform} post ideas, each spotlighting a different kind of customer who uses {brand}. For each one, list their situation, what they use {feature} for and the win they get. Keep the wins realistic and leave [brackets] for real numbers or quotes.",
  },
  {
    id: "objection-busters",
    category: "conversion",
    icon: "comment-check",
    title: "Objection busters",
    summary: "Turn the 7 reasons people hesitate into 7 content ideas.",
    platforms: POST_PLATFORMS,
    body: "List the 7 most common objections {audience} have before buying {feature}. Turn each one into a {platform} content idea that answers it with evidence, a story or data. Only use proof I can back up.",
  },
  {
    id: "no-product-needed",
    category: "conversion",
    icon: "screwdriver-wrench",
    title: "No product needed guide",
    summary: "Teach the fix for free, then mention your product once.",
    platforms: POST_PLATFORMS,
    body: "Write a {platform} post that explains how to improve {process} without buying anything. At the end, add one short line about how {brand} makes it easier, without sounding pushy.",
    defaults: { process: "your morning coffee at home" },
  },
  {
    id: "if-this-is-you",
    category: "conversion",
    icon: "user-check",
    title: "If this is you",
    summary: "Call out exactly who you built it for, then list the benefits.",
    platforms: POST_PLATFORMS,
    body: "Draft a {platform} post that starts: 'If you're a {role} dealing with {problem}, here's what we made for you.' Then list the benefits of {feature} in plain language.",
    defaults: { role: "office manager", problem: "a team that's sick of bad office coffee" },
  },
  {
    id: "launch-runway",
    category: "conversion",
    icon: "rocket-launch",
    title: "Launch runway",
    summary: "A 5-post sequence from tease to recap for your next launch.",
    platforms: POST_PLATFORMS,
    body: "Create a 5-post {platform} sequence for launching {feature}: 1) tease the problem, 2) show behind the scenes of making it, 3) announce it, 4) share first customer reactions, 5) recap with FAQs and next steps. Leave [brackets] for real customer quotes.",
  },

  // Short-form video
  {
    id: "hook-brainstorm",
    category: "short-video",
    icon: "bolt",
    title: "Hook brainstorm",
    summary: "20 scroll-stopping hooks under 10 words each.",
    platforms: VIDEO_PLATFORMS,
    body: "Generate 20 scroll-stopping hooks for short {platform} videos aimed at {audience} who struggle with {problem}. Each hook must be under 10 words and speak to one specific pain.",
  },
  {
    id: "sixty-second-explainer",
    category: "short-video",
    icon: "timer",
    title: "60-second explainer",
    summary: "Explain your simple system in 3 steps and 60 seconds.",
    platforms: VIDEO_PLATFORMS,
    body: "Write a 60-second {platform} script explaining our simple system for {process}. Include an attention-grabbing opening line, 3 clear steps and a closing call to check the link in our profile for the full walkthrough.",
  },
  {
    id: "pov-skits",
    category: "short-video",
    icon: "eye",
    title: "POV skits",
    summary: "5 'POV: you're a...' concepts your audience will recognise.",
    platforms: VIDEO_PLATFORMS,
    body: "Generate 5 POV-style {platform} video concepts that start with 'POV: you're a {role} and...' that {audience} will recognise. For each one, include the scenario, 2 or 3 shot ideas and a punchline.",
    defaults: { role: "home barista" },
  },
  {
    id: "plain-english-explainers",
    category: "short-video",
    icon: "chalkboard-user",
    title: "Plain English explainers",
    summary: "3 short scripts that explain one tricky term simply.",
    platforms: VIDEO_PLATFORMS,
    body: "Write 3 scripts for 45-second {platform} videos titled 'Understanding {metric} in plain English.' Explain what it is, why it matters and what good looks like for {audience}.",
  },
  {
    id: "hot-takes",
    category: "short-video",
    icon: "pepper-hot",
    title: "Hot take clips",
    summary: "5 short monologues with a strong opinion and one real example.",
    platforms: VIDEO_PLATFORMS,
    body: "Draft 5 short monologue scripts for {platform} where someone from {brand} shares a hot take about {topic}. Each needs a strong opening, one story or example, and a short takeaway.",
  },
  {
    id: "messy-to-sorted",
    category: "short-video",
    icon: "broom",
    title: "Messy to sorted",
    summary: "A satisfying before-and-after video with a beat-by-beat outline.",
    platforms: VIDEO_PLATFORMS,
    body: "Outline a short {platform} video that shows {process} going from messy to sorted. Include on-screen text ideas and a beat-by-beat outline.",
    defaults: { process: "our green bean storeroom" },
  },
  {
    id: "report-reveal",
    category: "short-video",
    icon: "chart-pie",
    title: "Report reveal",
    summary: "Open last month's numbers on camera: one surprise, one change.",
    platforms: VIDEO_PLATFORMS,
    body: "Write a 30-second {platform} script for 'opening last month's numbers' at {brand}. Share one surprising stat, what it means and what we'll change this month. Tone: honest, curious, data-driven. Use only the stat I give you and leave [brackets] where it goes.",
  },
  {
    id: "three-mistakes",
    category: "short-video",
    icon: "triangle-exclamation",
    title: "3 mistakes, 3 fixes",
    summary: "4 scripts on common mistakes and fixes people can try this week.",
    platforms: VIDEO_PLATFORMS,
    body: "Create 4 {platform} scripts for '3 mistakes you're making with {channel} and how to fix them.' Each fix should be something {audience} can do this week without spending money.",
  },
  {
    id: "old-way-skit",
    category: "short-video",
    icon: "masks-theater",
    title: "The old way skit",
    summary: "A funny skit: the chaos of the old way vs the calm of the new.",
    platforms: VIDEO_PLATFORMS,
    body: "Draft a funny short {platform} video showing the chaos of {workflow} the old way, then the calm of doing it properly. Include staged scenes and text overlays.",
    defaults: { workflow: "getting content approved over email and DMs" },
  },
  {
    id: "comment-qa",
    category: "short-video",
    icon: "microphone-lines",
    title: "Q&A from comments",
    summary: "5 quick answer videos built from the questions people ask most.",
    platforms: VIDEO_PLATFORMS,
    body: "Based on the questions {audience} ask most about {topic}, write 5 short {platform} Q&A video scripts. Each should state the question, give a short answer and say what to do next.",
  },
];

export function getTemplate(id: string): PromptTemplate | undefined {
  return TEMPLATES.find((t) => t.id === id);
}

export function templatesInCategory(category: Category): PromptTemplate[] {
  return TEMPLATES.filter((t) => t.category === category);
}

export interface ResolvedTemplate {
  body: string;
  platforms: readonly Platform[];
  defaults: SlotDefaults;
}

/** The body, platforms and defaults that apply for a business type. */
export function resolveTemplate(
  template: PromptTemplate,
  businessType?: BusinessType,
): ResolvedTemplate {
  const variant =
    businessType && template.variants && Object.hasOwn(template.variants, businessType)
      ? template.variants[businessType]
      : undefined;
  return {
    body: variant?.body ?? template.body,
    platforms: variant?.platforms ?? template.platforms,
    defaults: { ...template.defaults, ...variant?.defaults },
  };
}

const TOKEN = /\{([a-z]+)\}/g;

function isSlotKey(value: string): value is SlotKey {
  return (SLOT_KEYS as readonly string[]).includes(value);
}

/** Slot keys a body uses, in first-appearance order. */
export function slotsIn(body: string): SlotKey[] {
  const found: SlotKey[] = [];
  for (const match of body.matchAll(TOKEN)) {
    const key = match[1];
    if (isSlotKey(key) && !found.includes(key)) found.push(key);
  }
  return found;
}

export interface FillInputs {
  businessType?: BusinessType;
  platform?: Platform;
  /** Visitor values keyed by slot, including brand and audience. */
  values?: Partial<Record<Exclude<SlotKey, "platform">, string>>;
  /** Optional brand voice, appended as a closing line. */
  tone?: string;
  /** Optional content pillars, appended as a closing line. */
  pillars?: string;
}

export interface FillOptions {
  /**
   * What an empty slot becomes. "placeholder" (default) gives "[your audience]";
   * "example" uses the template's example value, for SEO pages and previews.
   */
  empty?: "placeholder" | "example";
}

function clean(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.replace(/\s+/g, " ").trim();
}

function stripEndPunctuation(value: string): string {
  return value.replace(/[.!?;,\s]+$/, "");
}

/** A run of a filled prompt: plain text, or the value standing in for one {slot} token. */
export interface FilledPart {
  text: string;
  slot?: SlotKey;
  /** For a slot part: the visitor gave a value, so it is not a bracketed placeholder. */
  filled?: boolean;
}

/**
 * fillTemplate as parts, so the builder can make each slot editable where it sits. Joining
 * the parts' text gives exactly fillTemplate's string. Never throws.
 */
export function fillTemplateParts(
  template: PromptTemplate,
  inputs: FillInputs = {},
  options: FillOptions = {},
): FilledPart[] {
  try {
    if (!template || typeof template.body !== "string") return [];
    const safeInputs: FillInputs = inputs && typeof inputs === "object" ? inputs : {};
    const businessType =
      safeInputs.businessType && BUSINESS_TYPES.includes(safeInputs.businessType)
        ? safeInputs.businessType
        : undefined;
    const platform =
      safeInputs.platform && PLATFORMS.includes(safeInputs.platform)
        ? safeInputs.platform
        : undefined;
    const resolved = resolveTemplate(template, businessType);
    const useExamples = options?.empty === "example";
    const values =
      safeInputs.values && typeof safeInputs.values === "object" ? safeInputs.values : {};

    const valueFor = (key: SlotKey): { text: string; filled: boolean } => {
      if (key === "platform") {
        if (platform) return { text: PLATFORM_META[platform].label, filled: true };
        if (useExamples && resolved.platforms[0]) {
          return { text: PLATFORM_META[resolved.platforms[0]].label, filled: true };
        }
        return { text: `[${SLOT_META.platform.placeholder}]`, filled: false };
      }
      const given = clean(values[key]);
      if (given) return { text: given, filled: true };
      if (useExamples) {
        return { text: resolved.defaults[key] ?? SLOT_META[key].example, filled: true };
      }
      return { text: `[${SLOT_META[key].placeholder}]`, filled: false };
    };

    const parts: FilledPart[] = [];
    const pushText = (text: string) => {
      if (text === "") return;
      const last = parts[parts.length - 1];
      if (last && last.slot === undefined) last.text += text;
      else parts.push({ text });
    };
    let last = 0;
    for (const match of resolved.body.matchAll(TOKEN)) {
      const start = match.index ?? 0;
      pushText(resolved.body.slice(last, start));
      last = start + match[0].length;
      const key = match[1];
      if (!isSlotKey(key)) {
        pushText(match[0]);
        continue;
      }
      const value = valueFor(key);
      // "a Instagram post" reads wrong, so swap the article for vowel-led values.
      const before = parts[parts.length - 1];
      if (/^[aeio]/i.test(value.text) && before && before.slot === undefined) {
        before.text = before.text.replace(/\b([Aa]) $/, "$1n ");
      }
      parts.push({ text: value.text, slot: key, filled: value.filled });
    }
    pushText(resolved.body.slice(last));

    const extras: string[] = [];
    const tone = stripEndPunctuation(clean(safeInputs.tone));
    if (tone) extras.push(`Write in our brand voice: ${tone}.`);
    const pillars = stripEndPunctuation(clean(safeInputs.pillars));
    if (pillars) extras.push(`Where it fits, tie the ideas to our content pillars: ${pillars}.`);
    if (extras.length > 0) pushText(`\n\n${extras.join(" ")}`);

    return parts;
  } catch {
    return typeof template?.body === "string" ? [{ text: template.body }] : [];
  }
}

/**
 * Fill a template for one visitor. Uses the business-type variant when there is one,
 * the visitor's values when given, and a bracketed placeholder otherwise. Never throws.
 */
export function fillTemplate(
  template: PromptTemplate,
  inputs: FillInputs = {},
  options: FillOptions = {},
): string {
  return fillTemplateParts(template, inputs, options)
    .map((part) => part.text)
    .join("");
}
